import { Session } from '@shopify/shopify-api';
import {
  Account,
  AccountStripeProfile,
  Campaign,
  CouponCode,
  Donation,
  DonationType,
  Feature,
  Invoice,
  Order,
  Organization,
  OrganizationReport,
  ReportOverview,
  ReportsApiReportsControllerGetOverviewReportRequest,
} from '@givz/core-client';
import { JwtPayload } from 'jwt-decode';

export type AuthContext = {
  accessToken: string;
  accountGid: string;
  isExpired: boolean;
  roles: ApiRole[];
};

export type Context = {
  accessToken?: string;
  accessTokenExpiration?: Date;
  authRetry?: boolean;
  isImpersonator?: boolean;
  redirectUrl?: string;
  session?: Session;
};

export enum AccountOnboardingStep {
  CHARITABLE_CAUSES = 'CHARITABLE_CAUSES',
  GOAL = 'GOAL',
  REFERRAL = 'REFERRAL',
}

// TODO - consider sharing these via core-client?
//  (we're also only using a subset here)
//
export type ApiRole = 'impersonator' | 'merchant';

export type AccessTokenPayload = {
  accountGid?: string;
  impersonatorGid?: string;
  roles: ApiRole[];
} & JwtPayload;

export interface CampaignWithReadOnly extends Campaign {
  readOnly?: boolean;
}

export interface DropdownOption {
  id: string;
  label: string | React.ReactNode;
  name: string;
}

export interface OrganizationsData {
  [organizationGid: string]: Organization;
}

export interface OrganizationWithId extends Organization {
  id: string;
}

export interface CouponCodeResourceItem extends Omit<CouponCode, 'id'> {
  id: string;
}

export interface CouponCodesData {
  [couponCodeId: string]: CouponCode;
}

export interface FormattedReportOverview
  extends Omit<
    ReportOverview,
    'salesGenerated' | 'amountDonated' | 'redemptionRate'
  > {
  salesGenerated: string;
  amountDonated: string;
  redemptionRate: string;
}

export interface FormattedOrganizationReport
  extends Omit<OrganizationReport, 'amountDonated'> {
  amountDonated: string;
}

export interface FormattedDonation
  extends Omit<
    Donation,
    'donationAmount' | 'pledgedAmount' | 'status' | 'fee'
  > {
  donationAmount: string;
  pledgedAmount: string;
  status: string;
  fee: string;
}

export interface FormattedInvoiceReport
  extends Omit<Invoice, 'issuedAt' | 'status' | 'total'> {
  issuedAt: string | undefined;
  status: string;
  total: string;
}

export interface FormattedQualifiedOrdersReport
  extends Omit<Order, 'total' | 'donation'> {
  total: string;
  donation: FormattedDonation | undefined;
}

export interface FormattedAccountStripeProfile
  extends Omit<AccountStripeProfile, 'additionalBillingContacts'> {
  additionalBillingContacts: string;
}

export interface FormattedAccount extends Omit<Account, 'stripeProfile'> {
  stripeProfile: FormattedAccountStripeProfile;
}

export type MapGivzFeatures = Record<string, Feature>;

export type PerformanceQueryParams =
  ReportsApiReportsControllerGetOverviewReportRequest & {
    donationGid?: string;
    donationsType?: DonationType;
    invoiceKey?: string;
    organizationGid?: string;
  };
